import { notify, confirm } from "../../utils/notify";
import React, { useState, useEffect, useMemo } from "react";
import Header from "../Header";
import { usersAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { MODULE_GROUPS, ROLE_CONFIG } from "../../utils/roles";
import { useListControls, Pagination } from "../../components/ListControls";
import Topbar from "../../components/Topbar";

const ROLES = [
  "employee",
  "manager",
  "hod",
  "finance",
  "procurement",
  "payroll",
  "hr",
  "admin",
];

const emptyForm = () => ({
  userName: "",
  employeeCode: "",
  email: "",
  mobile: "",
  department: "",
  designation: "",
  role: "employee",
  password: "",
  autoPassword: true,
  reportingManagerId: "",
  l2ManagerId: "",
  hodId: "",
  financeId: "",
  hrId: "",
  allowedModules: [],
});

function Avatar({ name }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        background: "#eef2ff",
        color: "#4f46e5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.782rem",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [genPwd, setGenPwd] = useState("");

  const load = () => {
    setLoading(true);
    usersAPI
      .list()
      .then((d) => setUsers(d.users || []))
      .catch((e) => notify.error(e.message || "Failed to load users"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);
  // Deep links from the Admin Console: ?new=1 opens "Add user",
  // ?edit=<id> opens that user's edit modal once the list has loaded.
  const [deepLinkDone, setDeepLinkDone] = useState(false);
  useEffect(() => {
    if (deepLinkDone || loading) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("new")) openCreate();
    else if (sp.get("edit")) {
      const u = users.find((x) => x._id === sp.get("edit"));
      if (u) openEdit(u);
    }
    setDeepLinkDone(true);
  }, [loading, users, deepLinkDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reporting Manager, L2 Manager, HOD, Finance and HR are assignment
  // *slots*, not fixed roles — literally any active user can be assigned
  // into any of these slots (approval routing is resolved from these direct
  // assignments, not from the assignee's own role). All five dropdowns share
  // the same full pool of active users, sorted by name; each option shows
  // the person's designation/role so the admin can tell who they're picking.
  const approverPool = useMemo(
    () =>
      users
        .filter((u) => u.status === "active")
        .slice()
        .sort((a, b) => (a.userName || "").localeCompare(b.userName || "")),
    [users],
  );
  const approverOptionLabel = (u) =>
    `${u.userName} — ${u.designation || ROLE_CONFIG[u.role]?.label || u.role}${u.department ? ` (${u.department})` : ""}`;

  // Kept for the KPI counter further down (counts of users by role).
  const managers = useMemo(
    () => users.filter((u) => u.role === "manager" && u.status === "active"),
    [users],
  );

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleModule = (key) => {
    setForm((p) => ({
      ...p,
      allowedModules: p.allowedModules.includes(key)
        ? p.allowedModules.filter((m) => m !== key)
        : [...p.allowedModules, key],
    }));
  };

  const toggleGroup = (groupKeys) => {
    const allSelected = groupKeys.every((k) => form.allowedModules.includes(k));
    setForm((p) => ({
      ...p,
      allowedModules: allSelected
        ? p.allowedModules.filter((m) => !groupKeys.includes(m))
        : Array.from(new Set([...p.allowedModules, ...groupKeys])),
    }));
  };

  const openCreate = () => {
    setForm(emptyForm());
    setEditId(null);
    setGenPwd("");
    setModal("ADD");
  };

  const openEdit = (u) => {
    setForm({
      userName: u.userName || "",
      employeeCode: u.employeeCode || "",
      email: u.email || "",
      mobile: u.mobile || "",
      department: u.department || "",
      designation: u.designation || "",
      role: u.role || "employee",
      password: "",
      autoPassword: false,
      reportingManagerId:
        u.reportingManagerId?._id || u.reportingManagerId || "",
      l2ManagerId: u.l2ManagerId?._id || u.l2ManagerId || "",
      hodId: u.hodId?._id || u.hodId || "",
      financeId: u.financeId?._id || u.financeId || "",
      hrId: u.hrId?._id || u.hrId || "",
      allowedModules: u.allowedModules || [],
    });
    setEditId(u._id);
    setGenPwd("");
    setModal("EDIT");
  };

  const handleSave = async () => {
    if (
      !form.userName ||
      !form.employeeCode ||
      !form.email ||
      !form.department ||
      !form.designation
    ) {
      notify.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        userName: form.userName,
        employeeCode: form.employeeCode,
        email: form.email,
        mobile: form.mobile,
        department: form.department,
        designation: form.designation,
        role: form.role,
        reportingManagerId: form.reportingManagerId || undefined,
        l2ManagerId: form.l2ManagerId || undefined,
        hodId: form.hodId || undefined,
        financeId: form.financeId || undefined,
        hrId: form.hrId || undefined,
        allowedModules: form.allowedModules,
      };
      if (editId) {
        await usersAPI.update(editId, payload);
        notify.success("User updated successfully!");
      } else {
        if (!form.autoPassword && form.password && form.password.length < 6) {
          notify.error("Password must be at least 6 characters");
          setSaving(false);
          return;
        }
        payload.password = form.autoPassword ? undefined : form.password;
        const res = await usersAPI.create(payload);
        if (res.generatedPassword) {
          setGenPwd(res.generatedPassword);
          setForm(emptyForm());
          notify.success(
            "User created! Share the generated password securely.",
          );
        } else {
          notify.success("User created and login access granted!");
          setForm(emptyForm());
          setModal(null);
        }
      }
      setEditId(null);
      load();
    } catch (e) {
      notify.error(e.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (u) => {
    const res = await confirm.action(
      u.status === "active"
        ? `Deactivate ${u.userName}?`
        : `Activate ${u.userName}?`,
      u.status === "active"
        ? "They will immediately lose login access."
        : "They will regain login access.",
      u.status === "active" ? "Yes, Deactivate" : "Yes, Activate",
      u.status === "active" ? "#dc2626" : "#16a34a",
    );
    if (!res.isConfirmed) return;
    try {
      if (u.status === "active") await usersAPI.deactivate(u._id);
      else await usersAPI.activate(u._id);
      notify.success("Status updated");
      load();
    } catch (e) {
      notify.error(e.message || "Failed");
    }
  };

  const handleResetPassword = async (u) => {
    const res = await confirm.resetPassword(`${u.userName} (${u.email})`);
    if (!res.isConfirmed) return;
    try {
      if (res.value.mode === "SET") {
        await usersAPI.setPassword(u._id, res.value.password, res.value.notifyUser);
        notify.success(`Password updated for ${u.userName}`);
      } else {
        const d = await usersAPI.sendResetLink(u._id);
        notify.success(d.message || `Reset link sent to ${u.email}`);
      }
    } catch (e) {
      notify.error(e.message || "Failed to reset password");
    }
  };

  const filtered = users.filter((u) => {
    const matchesQ =
      !q ||
      u.userName?.toLowerCase().includes(q.toLowerCase()) ||
      u.email?.toLowerCase().includes(q.toLowerCase()) ||
      u.employeeCode?.toLowerCase().includes(q.toLowerCase());
    const matchesRole = roleFilter === "ALL" || u.role === roleFilter;
    return matchesQ && matchesRole;
  });

  const lc = useListControls(filtered, { dateField: "createdAt", pageSize: 10 });

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <Topbar section="Admin Console" />
          <section className="container-fluid p-4 pt-3">
            <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <h5 className="text-white lh-1">User Management</h5>
                <div className="sub">
                  Create users, assign their approver hierarchy (Reporting
                  Manager, L2 Manager, HOD, Finance) and grant module-level
                  access
                </div>
              </div>
              <button
                onClick={openCreate}
                className="btn fw-semibold px-4 py-2"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  borderRadius: 10,
                  border: "1.5px solid rgba(255,255,255,0.35)",
                }}
              >
                <i className="bi bi-person-plus me-2" />
                Create User
              </button>
            </div>

            <div className="row g-2 mb-3">
              {[
                {
                  label: "Total Users",
                  val: users.length,
                  bg: "#eff6ff",
                  ic: "#3b82f6",
                  icon: "bi-people",
                },
                {
                  label: "Active",
                  val: users.filter((u) => u.status === "active").length,
                  bg: "#f0fdf4",
                  ic: "#22c55e",
                  icon: "bi-check-circle",
                },
                {
                  label: "Managers",
                  val: managers.length,
                  bg: "#fffbeb",
                  ic: "#f59e0b",
                  icon: "bi-person-badge",
                },
                {
                  label: "Inactive",
                  val: users.filter((u) => u.status === "inactive").length,
                  bg: "#fef2f2",
                  ic: "#ef4444",
                  icon: "bi-x-circle",
                },
              ].map((s, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="kpi-card d-flex align-items-center gap-2">
                    <div className="kpi-icon" style={{ background: s.bg }}>
                      <i className={`bi ${s.icon}`} style={{ color: s.ic }} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "1.17rem",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {s.val}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#6b7280",
                          marginTop: 2,
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <div className="search-bar" style={{ maxWidth: 320 }}>
                <i className="bi bi-search" style={{ color: "#9ca3af" }} />
                <input
                  placeholder="Search users..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {["ALL", ...ROLES].map((r) => (
                  <button
                    key={r}
                    className={`filter-chip ${roleFilter === r ? "active" : "inactive"}`}
                    onClick={() => setRoleFilter(r)}
                  >
                    {r === "ALL" ? "All Roles" : ROLE_CONFIG[r]?.label || r}
                  </button>
                ))}
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: "0.736rem" }}>
                <span style={{ color: "#9ca3af" }}><i className="bi bi-calendar3 me-1" />Joined</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={lc.dateFrom} onChange={(e) => lc.setDateFrom(e.target.value)} />
                <span style={{ color: "#9ca3af" }}>to</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={lc.dateTo} onChange={(e) => lc.setDateTo(e.target.value)} />
                {(lc.dateFrom || lc.dateTo) && (
                  <button className="btn btn-sm btn-light" onClick={() => { lc.setDateFrom(""); lc.setDateTo(""); }}>
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <span className="spinner-border text-primary" />
              </div>
            ) : lc.paginated.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-people" />
                <p>No users found</p>
              </div>
            ) : (
              lc.paginated.map((u, idx) => {
                const rc = ROLE_CONFIG[u.role] || {
                  color: "#6b7280",
                  bg: "#f9fafb",
                  label: u.role,
                };
                return (
                  <div key={u._id} className="record-card">
                    <div className="record-number">{(lc.page - 1) * lc.pageSize + idx + 1}.</div>
                    <div className="record-body">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <Avatar name={u.userName} />
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "0.874rem",
                            color: "#111827",
                          }}
                        >
                          {u.userName}
                        </div>
                        <span
                          className="status-pill"
                          style={{ background: rc.bg, color: rc.color }}
                        >
                          {rc.label}
                        </span>
                        <span
                          className={`status-pill ${u.status === "active" ? "status-approved" : "status-rejected"}`}
                        >
                          {u.status}
                        </span>
                        {u.allowedModules?.length > 0 && (
                          <span
                            style={{
                              background: "#f3f4f6",
                              color: "#6b7280",
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: 12,
                            }}
                          >
                            <i className="bi bi-shield-lock me-1" />
                            {u.allowedModules.length} module
                            {u.allowedModules.length !== 1 ? "s" : ""}{" "}
                            restricted
                          </span>
                        )}
                      </div>
                      <div className="row g-2">
                        <div className="col-12 col-md-4">
                          <div className="record-field">
                            <span className="rf-label">Emp Code — </span>
                            <span className="rf-value text-primary">
                              {u.employeeCode}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">Email — </span>
                            <span className="rf-value">{u.email}</span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">Mobile — </span>
                            <span className="rf-value">{u.mobile || "—"}</span>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="record-field">
                            <span className="rf-label">Dept — </span>
                            <span className="rf-value">{u.department}</span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">Designation — </span>
                            <span className="rf-value">{u.designation}</span>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="record-field">
                            <span className="rf-label">Reporting Mgr — </span>
                            <span className="rf-value">
                              {u.reportingManagerId?.userName || "—"}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">L2 Mgr — </span>
                            <span className="rf-value">
                              {u.l2ManagerId?.userName || "—"}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">HOD — </span>
                            <span className="rf-value">
                              {u.hodId?.userName || "—"}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">Finance — </span>
                            <span className="rf-value">
                              {u.financeId?.userName || "—"}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">HR — </span>
                            <span className="rf-value">
                              {u.hrId?.userName || "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="divider d-none d-md-block" />
                    <div className="record-actions">
                      <button
                        className="btn-card-view"
                        onClick={() => openEdit(u)}
                      >
                        Edit
                      </button>
                      <div className="btn-divider" />
                      <button
                        className="btn-card-view"
                        onClick={() => handleResetPassword(u)}
                        title="Set a new password or email a reset link"
                      >
                        <i className="bi bi-key me-1" />
                        Reset Password
                      </button>
                      <div className="btn-divider" />
                      <button
                        className={
                          u.status === "active"
                            ? "btn-card-reject"
                            : "btn-card-approve"
                        }
                        onClick={() => toggleStatus(u)}
                        disabled={u._id === me?.id}
                      >
                        {u.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="users"/>

            {modal && (
              <div
                className="modal show d-block"
                style={{ background: "rgba(0,0,0,0.45)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg">
                    <div className="modal-header border-0">
                      <h5 className="fw-bold">
                        <i className="bi bi-person-plus me-2 text-primary" />
                        {modal === "ADD" ? "Create New User" : "Edit User"}
                      </h5>
                      <button
                        className="btn-close"
                        onClick={() => {
                          setModal(null);
                          setEditId(null);
                        }}
                      />
                    </div>
                    <div className="modal-body">
                      {genPwd && (
                        <div>
                          <div className="alert alert-success py-2 small d-flex align-items-center justify-content-between">
                            <span>
                              <i className="bi bi-key me-1" />
                              <b>Generated password:</b> <code>{genPwd}</code> —
                              share this with the user securely.
                            </span>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => {
                                navigator.clipboard?.writeText(genPwd);
                                notify.info("Copied!");
                              }}
                            >
                              Copy
                            </button>
                          </div>
                          <div
                            className="text-center py-4"
                            style={{ color: "#6b7280", fontSize: "0.782rem" }}
                          >
                            <i
                              className="bi bi-check-circle"
                              style={{
                                fontSize: 40,
                                color: "#16a34a",
                                display: "block",
                                marginBottom: 10,
                              }}
                            />
                            User created successfully. Copy the password above
                            before closing — it won't be shown again.
                          </div>
                        </div>
                      )}

                      {!genPwd && (
                        <>
                          <div
                            className="fw-semibold small text-uppercase text-muted mb-2"
                            style={{ letterSpacing: 0.5 }}
                          >
                            Basic Details
                          </div>
                          <div className="row g-2 mb-3">
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Full Name *
                              </label>
                              <input
                                className="form-control"
                                value={form.userName}
                                onChange={(e) =>
                                  set("userName", e.target.value)
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Employee Code *
                              </label>
                              <input
                                className="form-control"
                                value={form.employeeCode}
                                onChange={(e) =>
                                  set("employeeCode", e.target.value)
                                }
                                disabled={!!editId}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Email *
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                                disabled={!!editId}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Mobile
                              </label>
                              <input
                                className="form-control"
                                value={form.mobile}
                                onChange={(e) => set("mobile", e.target.value)}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Department *
                              </label>
                              <input
                                className="form-control"
                                value={form.department}
                                onChange={(e) =>
                                  set("department", e.target.value)
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Designation *
                              </label>
                              <input
                                className="form-control"
                                value={form.designation}
                                onChange={(e) =>
                                  set("designation", e.target.value)
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Role *
                              </label>
                              <select
                                className="form-select"
                                value={form.role}
                                onChange={(e) => set("role", e.target.value)}
                              >
                                {ROLES.map((r) => (
                                  <option key={r} value={r}>
                                    {ROLE_CONFIG[r]?.label || r}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {!editId && (
                              <div className="col-md-6">
                                <label className="form-label small fw-semibold">
                                  Password
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="autoPwd"
                                      checked={form.autoPassword}
                                      onChange={(e) =>
                                        set("autoPassword", e.target.checked)
                                      }
                                    />
                                    <label
                                      className="form-check-label small"
                                      htmlFor="autoPwd"
                                    >
                                      Auto-generate
                                    </label>
                                  </div>
                                </div>
                                {!form.autoPassword && (
                                  <input
                                    type="text"
                                    className="form-control mt-1"
                                    placeholder="Min 6 characters"
                                    value={form.password}
                                    onChange={(e) =>
                                      set("password", e.target.value)
                                    }
                                  />
                                )}
                              </div>
                            )}
                          </div>

                          <div
                            className="fw-semibold small text-uppercase text-muted mb-2"
                            style={{ letterSpacing: 0.5 }}
                          >
                            Assign Hierarchy — Login Access
                          </div>
                          <div className="row g-2 mb-3">
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Reporting Manager
                              </label>
                              <select
                                className="form-select"
                                value={form.reportingManagerId}
                                onChange={(e) =>
                                  set("reportingManagerId", e.target.value)
                                }
                              >
                                <option value="">— Select Manager —</option>
                                {approverPool.map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {approverOptionLabel(m)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                L2 Manager
                              </label>
                              <select
                                className="form-select"
                                value={form.l2ManagerId}
                                onChange={(e) =>
                                  set("l2ManagerId", e.target.value)
                                }
                              >
                                <option value="">— Select L2 Manager —</option>
                                {approverPool.map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {approverOptionLabel(m)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                HOD
                              </label>
                              <select
                                className="form-select"
                                value={form.hodId}
                                onChange={(e) => set("hodId", e.target.value)}
                              >
                                <option value="">— Select HOD —</option>
                                {approverPool.map((h) => (
                                  <option key={h._id} value={h._id}>
                                    {approverOptionLabel(h)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                Finance
                              </label>
                              <select
                                className="form-select"
                                value={form.financeId}
                                onChange={(e) =>
                                  set("financeId", e.target.value)
                                }
                              >
                                <option value="">— Select Finance —</option>
                                {approverPool.map((f) => (
                                  <option key={f._id} value={f._id}>
                                    {approverOptionLabel(f)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-semibold">
                                HR
                              </label>
                              <select
                                className="form-select"
                                value={form.hrId}
                                onChange={(e) =>
                                  set("hrId", e.target.value)
                                }
                              >
                                <option value="">— Select HR —</option>
                                {approverPool.map((h) => (
                                  <option key={h._id} value={h._id}>
                                    {approverOptionLabel(h)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-12">
                              <div
                                style={{
                                  padding: "8px 12px",
                                  background: "#f8faff",
                                  borderRadius: 8,
                                  fontSize: "0.718rem",
                                  color: "#374151",
                                  border: "1px solid #e0e7ff",
                                }}
                              >
                                <i className="bi bi-info-circle me-1 text-primary" />
                                These are the specific approvers used in Travel
                                &amp; Vendor approval workflows for this user.
                              </div>
                            </div>
                          </div>

                          <div
                            className="fw-semibold small text-uppercase text-muted mb-2"
                            style={{ letterSpacing: 0.5 }}
                          >
                            Module Access
                          </div>
                          <div
                            className="mb-2"
                            style={{ fontSize: "0.718rem", color: "#6b7280" }}
                          >
                            Select which modules this user can access. Leave
                            everything unchecked to fall back to full default
                            role-based access.
                          </div>
                          <div className="row g-2">
                            {Object.entries(MODULE_GROUPS).map(
                              ([group, items]) => {
                                const groupKeys = items.map((i) => i.key);
                                const allSelected = groupKeys.every((k) =>
                                  form.allowedModules.includes(k),
                                );
                                return (
                                  <div key={group} className="col-12 col-md-4">
                                    <div
                                      style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 10,
                                        padding: 7,
                                        height: "100%",
                                      }}
                                    >
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="fw-semibold small">
                                          {group}
                                        </span>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-link p-0"
                                          style={{ fontSize: "0.7rem" }}
                                          onClick={() => toggleGroup(groupKeys)}
                                        >
                                          {allSelected ? "Clear" : "Select all"}
                                        </button>
                                      </div>
                                      {items.map((it) => (
                                        <div
                                          className="form-check"
                                          key={it.key}
                                        >
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={it.key}
                                            checked={form.allowedModules.includes(
                                              it.key,
                                            )}
                                            onChange={() =>
                                              toggleModule(it.key)
                                            }
                                          />
                                          <label
                                            className="form-check-label small"
                                            htmlFor={it.key}
                                          >
                                            {it.label}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="modal-footer border-0 gap-2">
                      <button
                        className="btn btn-light rounded-3 px-4"
                        onClick={() => {
                          setModal(null);
                          setEditId(null);
                        }}
                      >
                        {genPwd ? "Close" : "Cancel"}
                      </button>
                      {!genPwd && (
                        <button
                          className="btn rounded-3 px-5 fw-semibold"
                          style={{
                            background:
                              "linear-gradient(135deg,#1a5276,#1e8449)",
                            color: "#fff",
                            border: "none",
                          }}
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : modal === "ADD" ? (
                            "Create User"
                          ) : (
                            "Save Changes"
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}
export default UserManagement;
